import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, X } from "lucide-react";
import { Input, Textarea } from "../components/Input";
import Button from "../components/Button";
import type { Client } from "../types";

export default function NewCallPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const clientIdFromUrl = searchParams.get("clientId");
  const clientNameFromUrl = searchParams.get("clientName") || "";
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    clientName: clientNameFromUrl,
    companyName: "",
    scheduledDate: "",
    scheduledTime: "",
    isVirtual: true,
    location: "",
    goals: "",
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await fetch("/api/clients");
      if (response.ok) {
        const data = await response.json();
        setClients(data);
        
        if (clientIdFromUrl) {
          const preselectedClient = data.find((c: Client) => c.id === parseInt(clientIdFromUrl));
          if (preselectedClient) {
            setSelectedClient(preselectedClient);
          }
        }
      }
    } catch (error) {
      console.error("Error loading clients:", error);
    }
  };

  const filteredClients = clients.filter((client) => {
    const query = clientSearch.toLowerCase();
    return (
      client.name.toLowerCase().includes(query) ||
      (client.company || "").toLowerCase().includes(query)
    );
  });

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setFormData({ ...formData, clientName: client.name });
    setClientSearch("");
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setFormData({ ...formData, clientName: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientName || !formData.scheduledDate || !formData.scheduledTime) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    const scheduledAt = new Date(
      `${formData.scheduledDate}T${formData.scheduledTime}`
    ).toISOString();

    try {
      const response = await fetch("/api/calls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: selectedClient?.id || (clientIdFromUrl ? parseInt(clientIdFromUrl) : undefined),
          clientName: formData.clientName,
          scheduledAt,
          isVirtual: formData.isVirtual,
          location: formData.isVirtual ? undefined : formData.location,
          goals: formData.goals || undefined,
        }),
      });

      if (response.ok) {
        const newCall = await response.json();
        navigate(`/calls/${newCall.id}`);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to create call");
      }
    } catch (err) {
      setError("Failed to create call. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate("/calls")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-medium mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Calls
        </button>
        <h1 className="text-2xl font-bold text-slate-900">Schedule New Call</h1>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Select Existing Client
          </label>
          {selectedClient ? (
            <div className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <div>
                <p className="font-medium text-teal-900">{selectedClient.name}</p>
                {selectedClient.company && (
                  <p className="text-sm text-teal-700">{selectedClient.company}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleClearClient}
                className="p-1 text-teal-600 hover:text-teal-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  setShowClientDropdown(true);
                }}
                onFocus={() => setShowClientDropdown(true)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
              {showClientDropdown && filteredClients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredClients.slice(0, 10).map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleSelectClient(client)}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <p className="font-medium text-slate-900">{client.name}</p>
                      {client.company && (
                        <p className="text-sm text-slate-500">{client.company}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="text-xs text-slate-500">
            Or enter a new client name below
          </p>
        </div>

        <Input
          label="Client Name *"
          placeholder="e.g., John Smith"
          value={formData.clientName}
          onChange={(e) => {
            setFormData({ ...formData, clientName: e.target.value });
            if (selectedClient && e.target.value !== selectedClient.name) {
              setSelectedClient(null);
            }
          }}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Date *"
            type="date"
            value={formData.scheduledDate}
            onChange={(e) =>
              setFormData({ ...formData, scheduledDate: e.target.value })
            }
            required
          />
          <Input
            label="Time *"
            type="time"
            value={formData.scheduledTime}
            onChange={(e) =>
              setFormData({ ...formData, scheduledTime: e.target.value })
            }
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Meeting Type
          </label>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="meetingType"
                checked={formData.isVirtual}
                onChange={() => setFormData({ ...formData, isVirtual: true })}
                className="w-4 h-4 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-slate-700">Virtual</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="meetingType"
                checked={!formData.isVirtual}
                onChange={() => setFormData({ ...formData, isVirtual: false })}
                className="w-4 h-4 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-slate-700">In-Person</span>
            </label>
          </div>
        </div>

        {!formData.isVirtual && (
          <Input
            label="Location"
            placeholder="e.g., 123 Main St, Suite 100"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
          />
        )}

        <Textarea
          label="Meeting Goals"
          placeholder="What do you want to achieve in this meeting?"
          value={formData.goals}
          onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
          rows={4}
        />

        <div className="flex gap-3 pt-4 border-t border-slate-100">
          <Button type="submit" loading={isSubmitting} className="flex-1">
            Create Call
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/calls")}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
